// Copyright - Kyley Harris (2021) - All rights to copy and reuse allowed
import * as db from './indexed-db';
import type { GetOptions } from './indexed-db';
import { LangIndexedDbWrapper } from '../../localization/webmodule-common/database/indexed-db-wrapper-lang';

// Simple UI notifier; replace with your own UX layer if needed.
function information(title: string, message: string) {
  alert(`${title} ${message}`);
}

export class IndexedDBAccess {
  protected _connection: IDBDatabase | null = null;

  // single-flight guard so concurrent callers share the same in-flight open()
  private _connecting: Promise<IDBDatabase> | null = null;

  // connection pooling - holds connection open for burst operations
  private _closeTimeout: ReturnType<typeof setTimeout> | null = null;
  private _connectionIdleTime: number = 30000; // 30 seconds in milliseconds

  // prevent alert spam if multiple events fire
  private _blockedNotified = false;
  private _versionChangeNotified = false;

  public dbName(): string {
    throw new Error('override dbname');
  }

  protected version(): number {
    let version = 1;
    this.getDatabaseUpgrades().forEach(x => (version = Math.max(version, x.version)));
    return version;
  }

  // return a list of upgrades that get this database to the correct version
  protected getDatabaseUpgrades(): db.DatabaseUpgrade[] {
    throw new Error('Method not implemented.');
  }

  protected close(): void {
    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
      this._closeTimeout = null;
    }

    if (this._connection) {
      try {
        this._connection.close();
      } catch {
        // noop
      } finally {
        this._connection = null;
      }
    }
  }

  protected delayedClose(): void {
    // Clear any existing timeout
    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
    }

    // Set new timeout for delayed closing
    this._closeTimeout = setTimeout(() => {
      this.close();
    }, this._connectionIdleTime);
  }

  // Add method to force immediate close when needed
  public forceClose(): void {
    this.close();
  }

  public async connect(): Promise<IDBDatabase> {
    const startTime = performance.now();
    let connectionType = 'new';

    try {
      if (this._connection) {
        connectionType = 'reused';
        db.log('Database connection reused');
        return this._connection;
      }
      if (this._connecting) {
        connectionType = 'shared';
        db.log('Database open already in progress; awaiting existing attempt');
        this._connection = await this._connecting;
        return this._connection;
      }

      this._blockedNotified = false;
      this._versionChangeNotified = false;

      this._connecting = db.openDb({
        dbName: this.dbName(),
        version: this.version(),
        upgrades: this.getDatabaseUpgrades(),
        onBlocked: () => {
          if (!this._blockedNotified) {
            this._blockedNotified = true;
            information(LangIndexedDbWrapper.databaseAccessBlocked(), LangIndexedDbWrapper.closeTabs());
          }
        }
      });

      const conn = await this._connecting;

      // openDb already wires some events, but we add a user-facing notification here.
      conn.onversionchange = () => {
        // Force immediate close of the connection on version change
        this.forceClose();
        db.log('A new version of the database is ready, please reload this page');
        if (!this._versionChangeNotified) {
          this._versionChangeNotified = true;
          information(LangIndexedDbWrapper.applicationUpdate(), LangIndexedDbWrapper.reloadToContinue());
        }
      };

      this._connection = conn;
      return conn;
    } finally {
      // ensure the single-flight flag is cleared even if open failed
      this._connecting = null;

      // Log performance metrics
      const endTime = performance.now();
      const duration = endTime - startTime;
      db.log(`IndexedDB connection (${connectionType}) took ${duration.toFixed(2)}ms for ${this.dbName()}`);
    }
  }

  public async getFirst<T>(objectStore: string, options?: GetOptions): Promise<T | null> {
    const conn = await this.connect();
    try {
      return await db.get<T>(conn, objectStore, options);
    } finally {
      this.delayedClose();
    }
  }

  public store(type: { name: string }): string {
    return type.name;
  }

  public async getAll<T>(objectStore: string, options?: GetOptions): Promise<T[]> {
    const conn = await this.connect();
    try {
      return await db.getAll<T>(conn, objectStore, options);
    } finally {
      this.delayedClose();
    }
  }

  public async put<T extends { id?: IDBValidKey }>(item: T, objectStore: string): Promise<boolean> {
    const conn = await this.connect();
    try {
      await db.put<T>(conn, objectStore, item);
      return true;
    } catch {
      return false;
    } finally {
      this.delayedClose();
    }
  }

  public async putMany<T extends { id?: IDBValidKey }>(items: T[], objectStore: string): Promise<boolean> {
    const conn = await this.connect();
    try {
      await db.putAll<T>(conn, objectStore, items);
      return true;
    } catch {
      return false;
    } finally {
      this.delayedClose();
    }
  }

  public async del(id: IDBValidKey, objectStore: string): Promise<void> {
    const conn = await this.connect();
    try {
      await db.del(conn, objectStore, id);
    } finally {
      this.delayedClose();
    }
  }
}
